import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { getAppointments } from '../api/appointments';
import { getPriceMatchItem, createPriceMatch, getLivePriceMatches, getPastPriceMatches } from '../api/priceMatch';

// Order Ops → Price Match.
//
// A customer shows the same piece cheaper elsewhere; we lower that one piece's
// ZRP and saver price while it is out with the rider. ZOCS puts the old prices
// back by itself when the piece is reversed, handed on, or sold and later
// returned. Only pieces in transit can be matched.

const STATUS_LABEL = {
  Live: 'Live',
  Sold_at_matched_price: 'Sold at matched price',
  Old_price_restored: 'Old price restored',
  Returned_old_price_restored: 'Returned · old price restored',
};
const STATUS_CLASS = {
  Live: 'bg-zoddle-blue/15 text-zoddle-blue',
  Sold_at_matched_price: 'bg-zoddle-green/15 text-green-700',
  Old_price_restored: 'bg-gray-100 text-gray-600',
  Returned_old_price_restored: 'bg-zoddle-pink/15 text-zoddle-pink',
};

const rupees = v => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const istTime = iso => (iso
  ? new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  : '—');

function NewPriceMatch({ onCreated }) {
  const [barcode, setBarcode] = useState('');
  const [item, setItem] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [orders, setOrders] = useState([]);
  const [uid, setUid] = useState('');
  const [price, setPrice] = useState('');
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    const today = new Date().toLocaleDateString('en-CA');
    getAppointments({ date_from: today, date_to: today, show_all: true })
      .then(rows => setOrders((rows || [])
        .filter(a => a.zcp_appointment_uid && a.operational_status !== 'cancelled')
        .sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))))
      .catch(() => setOrders([]));
  }, []);

  async function lookup() {
    const bc = barcode.trim();
    setItem(null); setLookupError(''); setError(''); setDone(null);
    if (!bc) return;
    try { setItem(await getPriceMatchItem(bc)); }
    catch (e) { setLookupError(e.response?.data?.detail || 'Lookup failed'); }
  }

  const blockReason = !item ? null
    : item.live_match ? `This piece already has a live price match (${item.live_match}).`
    : !item.in_transit ? `Only pieces out with a rider can be matched — this one is "${item.status}".`
    : null;
  const priceNum = Number(price);
  const canSubmit = item && !blockReason && price !== '' && priceNum > 0 && !saving;

  async function submit() {
    setSaving(true); setError('');
    try {
      const r = await createPriceMatch({ zoddle_barcode: item.zoddle_barcode, appointment_uid: uid || null,
                                         matched_price: priceNum, link: link.trim() || null });
      setDone(r);
      setBarcode(''); setItem(null); setUid(''); setPrice(''); setLink('');
      onCreated?.();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save the price match'); }
    finally { setSaving(false); }
  }

  const label = 'block text-xs font-semibold text-gray-500 mb-1';
  const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green';

  return (
    <div className="max-w-lg bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      {done && (
        <div className="bg-zoddle-green/10 border border-zoddle-green/30 rounded-lg px-3 py-2 text-sm text-green-800">
          {done.reference} saved — {done.zoddle_barcode} now {rupees(done.matched_price)} (was {rupees(done.old_zoddle_price)}).
        </div>
      )}
      <div>
        <label className={label}>Zoddle Barcode</label>
        <input value={barcode} onChange={e => setBarcode(e.target.value)} onBlur={lookup}
          onKeyDown={e => e.key === 'Enter' && lookup()} placeholder="Scan or type the barcode" className={input} />
        {lookupError && <p className="text-xs text-zoddle-pink mt-1">{lookupError}</p>}
      </div>

      {item && (
        <div className="flex gap-3 items-center bg-gray-50 rounded-lg p-3">
          {item.image_url
            ? <img src={cldThumb(item.image_url, 300)} alt="" className="w-14 h-14 object-cover rounded" />
            : <div className="w-14 h-14 rounded bg-gray-200" />}
          <div className="text-xs text-gray-600">
            <p className="font-semibold text-gray-800">{item.brand || '—'}</p>
            <p>{item.design_name || ''}{item.size ? ` · ${item.size}` : ''}</p>
            <p className="text-gray-400">Status: {item.status}</p>
          </div>
        </div>
      )}
      {blockReason && <p className="text-xs text-zoddle-pink font-medium">{blockReason}</p>}

      <div>
        <label className={label}>Order UID</label>
        <select value={uid} onChange={e => setUid(e.target.value)} className={input}>
          <option value="">— pick today's order —</option>
          {orders.map(a => (
            <option key={a.appointment_id} value={a.zcp_appointment_uid}>
              {a.customer_name || 'No name'} · {a.zcp_appointment_uid}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Current ZRP</label>
          <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg text-gray-700">{item ? rupees(item.zoddle_price) : '—'}</div>
        </div>
        <div>
          <label className={label}>Current Saver price</label>
          <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg text-gray-700">{item ? rupees(item.saver_price) : '—'}</div>
        </div>
      </div>
      {item && item.current_charge != null && (
        <p className="text-xs text-gray-400 -mt-2">Customer is charged {rupees(item.current_charge)} today. The temp ZRP must be lower.</p>
      )}

      <div>
        <label className={label}>Temp ZRP</label>
        <input type="number" min="1" step="1" value={price} onChange={e => setPrice(e.target.value)}
          placeholder="Matched price" className={input} />
      </div>
      <div>
        <label className={label}>Link</label>
        <input value={link} onChange={e => setLink(e.target.value)}
          placeholder="Where the cheaper price was seen" className={input} />
      </div>

      {error && <p className="text-xs text-zoddle-pink font-medium">{error}</p>}
      <button onClick={submit} disabled={!canSubmit}
        className="w-full bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
        {saving ? 'Saving...' : 'Submit price match'}
      </button>
    </div>
  );
}

function MatchTable({ rows, past }) {
  if (!rows.length) {
    return <div className="text-center py-16 text-gray-400 text-sm">{past ? 'No past price matches yet' : 'No live price matches'}</div>;
  }
  const th = 'text-left px-3 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap';
  const td = 'px-3 py-2.5 text-sm text-gray-700 whitespace-nowrap';
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className={th}>Price match ID</th>
            <th className={th}>Zoddle Barcode</th>
            <th className={th}>Initiated</th>
            <th className={th}>Initiated by</th>
            <th className={th}>Order UID</th>
            <th className={th}>ZRP at match</th>
            <th className={th}>ZRP after match</th>
            <th className={th}>Link</th>
            <th className={th}>Status</th>
            {past && <th className={th}>Settled</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.reference} className="border-b border-gray-100 last:border-0">
              <td className={`${td} font-semibold`}>{r.reference}</td>
              <td className={td}>{r.zoddle_barcode}</td>
              <td className={td}>{istTime(r.initiated_at)}</td>
              <td className={td}>{r.initiated_by || '—'}</td>
              <td className={td}>{r.appointment_uid || '—'}</td>
              <td className={td}>
                {rupees(r.old_zoddle_price)}
                {r.old_saver_price != null && Number(r.old_saver_price) !== Number(r.old_zoddle_price) && (
                  <span className="text-xs text-gray-400"> (saver {rupees(r.old_saver_price)})</span>
                )}
              </td>
              <td className={td}>{rupees(r.matched_price)}</td>
              <td className={td}>
                {r.link ? <a href={r.link} target="_blank" rel="noreferrer" className="text-zoddle-blue hover:underline">Open</a> : '—'}
              </td>
              <td className={td}>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLASS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                  {STATUS_LABEL[r.status] || r.status}
                </span>
              </td>
              {past && (
                <td className={td} title={r.settled_note || ''}>
                  {istTime(r.settled_at)}
                  {r.settled_note && <p className="text-xs text-gray-400 max-w-[220px] truncate">{r.settled_note}</p>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PriceMatchTab() {
  const [view, setView] = useState('new'); // new | live | past
  const [live, setLive] = useState([]);
  const [past, setPast] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(false);

  const loadLive = useCallback(async () => {
    setLoading(true);
    try { setLive(await getLivePriceMatches()); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  const loadPast = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const data = await getPastPriceMatches(offset, 50);
      setPast(prev => (offset === 0 ? data : { total: data.total, rows: [...prev.rows, ...data.rows] }));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'live') loadLive();
    if (view === 'past') loadPast(0);
  }, [view, loadLive, loadPast]);

  const pill = v => `text-sm font-semibold px-4 py-2 rounded-lg transition ${
    view === v ? 'bg-zoddle-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button className={pill('new')} onClick={() => setView('new')}>New price match</button>
        <button className={pill('live')} onClick={() => setView('live')}>Live price matches</button>
        <button className={pill('past')} onClick={() => setView('past')}>Past price matches</button>
        {view !== 'new' && (
          <button onClick={() => (view === 'live' ? loadLive() : loadPast(0))}
            className="ml-auto text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
        )}
      </div>

      {view === 'new' && <NewPriceMatch />}
      {view !== 'new' && loading && !(view === 'past' && past.rows.length) && (
        <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      )}
      {view === 'live' && !loading && <MatchTable rows={live} />}
      {view === 'past' && (past.rows.length > 0 || !loading) && (
        <>
          <MatchTable rows={past.rows} past />
          {past.rows.length < past.total && (
            <button onClick={() => loadPast(past.rows.length)} disabled={loading}
              className="mt-3 text-sm text-zoddle-blue font-semibold hover:underline disabled:opacity-50">
              {loading ? 'Loading...' : `Load more (${past.total - past.rows.length} left)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
